import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconArrowCircleLeft } from "blocks-nextjs/blocks-react/components/Icons/ArrowCircleLeft/ArrowCircleLeft";
import { CatIconClose } from "blocks-nextjs/blocks-react/components/Icons/Close/Close";
import { CatIconInfo } from "blocks-nextjs/blocks-react/components/Icons/Info/Info";
import { CatIconReorder } from "blocks-nextjs/blocks-react/components/Icons/Reorder/Reorder";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatSegmentedControl } from "blocks-nextjs/blocks-react/components/SegmentedControl/SegmentedControl";
import { CatSegmentedControlItem } from "blocks-nextjs/blocks-react/components/SegmentedControlItem/SegmentedControlItem";
import { CatTab } from "blocks-nextjs/blocks-react/components/Tab/Tab";
import { CatTabPanel } from "blocks-nextjs/blocks-react/components/TabPanel/TabPanel";
import { CatTabs } from "blocks-nextjs/blocks-react/components/Tabs/Tabs";
import { CatTooltip } from "blocks-nextjs/blocks-react/components/Tooltip/Tooltip";
//import { BlocksSegmentItemChange } from "blocks-nextjs/blocks-web-components/dist/bedrock/events";
import { format } from "date-fns-tz";
import React, { lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import APIError from "../../components/APIError/APIError";
import AiAssistant from "../../components/AiAssistant/AiAssistant";
import AssetMergeButton from "../../components/AssetPortfolio/AssetMergeButton";
import AssetPortfolio from "../../components/AssetPortfolio/AssetPortfolio";
import ProjectDetailsMaps from "../../components/GoogleMaps/ProjectDetailsMaps";
import Metrics from "../../components/Metrics/Metrics";
import NetworkDiagram from "../../components/NetworkDiagram/NetworkDiagram";
import OpsPlanner from "../../components/OpsPLanner/OpsPlanner";
import SankeyChart from "../../components/SankeyDiagram/SankeyChart";
import { SankeyDiagram } from "../../components/SankeyDiagram/SankeyDiagram";
import { CaasRoute } from "../../routes";
import { useProjectAssetChargeActivitiesQuery, useProjectAssetsDetailsQuery, useProjectEnergyActivitiesQuery, useProjectEnergyMetricsQuery, useProjectListQuery, useProjectNetworkQuery, useProjectPowerSocTrendsQuery, useUsageHistoryDetailsQuery } from "../../services/query/project-asset-details.query";
import { httpStatus } from "../../shared/constants";
import { GHOST_ASSET_ENTRIES } from "../../shared/ghostData";
import { encodeProjectDetailsQuery } from "../../shared/helper";
import { PageEvents } from "../../shared/pageEvents";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { getValidTimezone, safeFormat, safeToZonedTime } from "../../utils/dateFormatter";
import { MetricsEnergyEnum } from "./MetricsData";
import "./ProjectDetails.css";
import { extractHourlyEnergyData, getAssetMapIcon, getNetworkEdges, getNetworkNodes, prepareSankeyChartData, prepareSankeyData } from "./ProjectDetails.helper";
import { getBarChartData } from "./UsageHistoryBarChartData";
const ActivityTimeline = lazy(() => import("../../components/ActivityTimeline/ActivityTimeline"));
const EnergyChart = lazy(() => import("../../components/EnergyChart/EnergyChart"));
const EnergyProductivity = lazy(() => import("../../components/EnergyProductivity/EnergyProductivity"));
const NoDataMessage = lazy(() => import("../../components/DataError/NoDataMessage"));
const UsageHistory = lazy(() => import("../../components/UsageHistory/UsageHistory"));
/**
 * Best-effort wrappers around the native <dialog> modal API. showModal/close can
 * throw (dialog already open/closed, or the API missing in some browsers), so we
 * guard them and fall back to toggling the `open` attribute to keep the
 * full-screen overlay from crashing the page at runtime.
 */
const openDialogSafely = (dialog) => {
    try {
        if (!dialog.open && !dialog.hasAttribute("open")) {
            dialog.showModal();
        }
    }
    catch {
        // Fallback: ensure the dialog becomes visible even if showModal() is unsupported/throws.
        dialog.setAttribute("open", "");
    }
};
const closeDialogSafely = (dialog) => {
    try {
        if (dialog.open || dialog.hasAttribute("open")) {
            dialog.close();
        }
    }
    catch {
        // Fallback: ensure the dialog is hidden even if close() throws.
        dialog.removeAttribute("open");
    }
};
const ProjectDetails = () => {
    // setAssetDetails is removed from the below destruct variables due to demo purpose
    const { assetChargeActivityData, assetEnergyActivityData, assetTrendsData, historicDataDate, networkDiagramData, networkNodes, projectDetails, projectMetaData, setAssetChargeActivityData, setAssetEnergyActivityData, setAssetTrendsData, setAssetsDetailsBySort, setHistoricDataDate, setNetworkDiagramData, setNetworkEdges, setNetworkNodes, setProjectDetails, setProjectMetaData } = useProjectDetailsStore((state) => ({
        assetChargeActivityData: state.assetChargeActivityData,
        assetEnergyActivityData: state.assetEnergyActivityData,
        assetTrendsData: state.assetTrendsData,
        historicDataDate: state.historicDataDate,
        networkDiagramData: state.networkDiagramData,
        networkEdges: state.networkEdges,
        networkNodes: state.networkNodes,
        projectDetails: state.projectDetails,
        projectMetaData: state.projectMetaData,
        setAssetChargeActivityData: state.setAssetChargeActivityData,
        setAssetDetails: state.setAssetDetails,
        setAssetEnergyActivityData: state.setAssetEnergyActivityData,
        setAssetTrendsData: state.setAssetTrendsData,
        setAssetsDetailsBySort: state.setAssetsDetailsBySort,
        setHistoricDataDate: state.setHistoricDataDate,
        setNetworkDiagramData: state.setNetworkDiagramData,
        setNetworkEdges: state.setNetworkEdges,
        setNetworkNodes: state.setNetworkNodes,
        setProjectDetails: state.setProjectDetails,
        setProjectMetaData: state.setProjectMetaData
    }));
    const navigate = useNavigate();
    const params = useParams();
    // Get timezone - use IANA timezone name only (e.g., "Europe/Amsterdam")
    // IMPORTANT: Do NOT use offset field for timezone conversions as it's a UTC offset string
    // The offset field (e.g., "UTC+01:00") is only for UI display, not for date-fns-tz functions
    const projectDetailsTimezone = projectDetails?.["timeZone"] || projectDetails?.timezone;
    // Validate and use the timezone with safe conversion
    const validatedTimezone = getValidTimezone(projectDetailsTimezone);
    const previousDay = new Date();
    previousDay.setDate(previousDay.getDate() - 1);
    const formattedPreviousDay = format(previousDay, "MM/dd/yyyy");
    const currentDay = new Date();
    // Safely convert to zoned time with error handling
    const cestDate = safeToZonedTime(currentDay, validatedTimezone || null);
    currentDay.setDate(currentDay.getDate());
    const [searchParams, setSearchParams] = useSearchParams();
    const liveDashboardDate = safeFormat(cestDate, "dd MMM yyyy");
    const [opsPlannerDate, setOpsPlannerDate] = useState("");
    const [refetchDataFromApi, setRefetchDataFromApi] = useState(undefined);
    const [selectedDate, setSelectedDate] = useState(undefined);
    const [selectedAddress, setSelectedAddress] = useState();
    const [metricsDate, setMetricsDate] = useState(liveDashboardDate);
    const [usageHistoryMetricsFilter, setUsageHistoryMetricsFilter] = useState("Weekly");
    const [usageHistoryDataAvailable, setUsageHistoryDataAvailable] = useState(undefined);
    const [usageHistoryHistogramData, setUsageHistoryHistogramData] = useState([]);
    const [addressDetails, setAddressDetails] = useState([]);
    const urlPanel = searchParams.get("panel");
    const validPanels = ["Timeline", "SankeyDiagram", "Replay"];
    const initialHistoricPanel = validPanels.includes(urlPanel) ? urlPanel : "Timeline";
    const panelIndexMap = {
        Replay: 2,
        SankeyDiagram: 1,
        Timeline: 0
    };
    const initialHistoricIndex = panelIndexMap[initialHistoricPanel];
    const [historicDataPanel, setHistoricDataPanel] = useState(initialHistoricPanel);
    const [historicDataPanelActiveIndex, setHistoricDataPanelActiveIndex] = useState(initialHistoricIndex);
    const [historicChartPanel, setHistoricChartPanel] = useState("EnergyChart");
    const [historicChartPanelActiveIndex, setHistoricChartPanelActiveIndex] = useState(0);
    const [fetchHistoryData, setFetchHistoryData] = useState(false);
    const [isFullScreen, setFullScreen] = useState(false);
    const [replayTimeline, setReplayTimeline] = useState(0);
    const [replayTimelineValue, setReplayTimelineValue] = useState(0);
    const [orderByAsset, setOrderByAsset] = useState("");
    const [orderBySoc, setOrderBySoc] = useState("");
    const [sankeyData, setSankeyData] = useState({
        customData: [],
        labels: [],
        links: { source: [], target: [], value: [] },
        positions: { x: [], y: [] }
    });
    const initialTab = searchParams.get("tab");
    const tabIndexMap = {
        Historic: 1,
        Live: 0,
        OpsPlanner: 2,
        // eslint-disable-next-line perfectionist/sort-objects
        AIInsights: 3
    };
    const initialCatTabsActiveIndex = tabIndexMap[initialTab] ?? 0;
    const [catTabsActiveIndex, setCatTabsActiveIndex] = useState(initialCatTabsActiveIndex);
    const mergeParams = (updates) => {
        const params = Object.fromEntries(searchParams.entries());
        Object.entries(updates).forEach(([k, v]) => {
            if (v === null)
                delete params[k];
            else
                params[k] = v;
        });
        setSearchParams(params, { replace: true });
    };
    const [panel, setPanel] = useState(initialHistoricPanel);
    useEffect(() => {
        const tab = searchParams.get("tab");
        if (tab === "Historic") {
            setCatTabsActiveIndex(1);
            setFetchHistoryData(true);
            setMetricsDate(historicDataDate ?? formattedPreviousDay);
            if (panel === "Replay") {
                setHistoricDataPanel("Timeline");
                setHistoricDataPanelActiveIndex(0);
                setReplayTimeline(1);
            }
            mergeParams({ panel });
        }
        else if (tab === "OpsPlanner") {
            const chargingDrawer = searchParams.get("ChargingDrawer");
            mergeParams({ ChargingDrawer: chargingDrawer });
            setCatTabsActiveIndex(2);
            setFetchHistoryData(false);
            setMetricsDate(liveDashboardDate);
        }
        else if (tab === "AIInsights") {
            const chargingDrawer = searchParams.get("ChargingDrawer");
            mergeParams({ ChargingDrawer: chargingDrawer });
            setCatTabsActiveIndex(3);
            // setFetchHistoryData(false);
            // setMetricsDate(liveDashboardDate);
        }
        else {
            mergeParams({ tab: "Live" });
            setCatTabsActiveIndex(0);
            setFetchHistoryData(false);
            setMetricsDate(liveDashboardDate);
        }
    }, [searchParams, panel]);
    const [sankeyChartData, setSankeyChartData] = useState({
        links: [],
        nodes: []
    });
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const isCatUser = primaryPartyType?.toUpperCase() === "CAT";
    const queryParamsProject = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber }),
        projectStatus: "ACTIVE"
    };
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const queryParamsAssetDetails = {
        ...(orderByAsset && { assetName: orderByAsset }),
        ...(orderBySoc && { assetId: orderBySoc }),
        ...(selectedDate && catTabsActiveIndex === 1 && { startDate: selectedDate })
    };
    const [isAddressDetailsLoaded, setIsAddressDetailsLoaded] = useState(false);
    const handleReplayClick = (value) => {
        if (historicDataPanel === "SankeyDiagram" && value === 1) {
            setHistoricDataPanelActiveIndex(0);
            setHistoricDataPanel("Timeline");
            setReplayTimeline(1);
        }
        else if (value === 0) {
            setReplayTimeline(0);
        }
        else {
            setReplayTimeline(replayTimeline + 1);
        }
    };
    const { data: { projects = [] } = {}, error: useProjectListQueryError, isError } = useProjectListQuery(encodeProjectDetailsQuery(queryParamsProject, true), userPartyNumber, true);
    if (isError && useProjectListQueryError?.response?.status === httpStatus.ERROR_403) {
        navigate("/restricted-access");
    }
    const selectedProject = useMemo(() => {
        const project = projects?.find((p) => p.projectId === params.id);
        return project?.projectName ?? projectDetails?.projectName ?? "";
    }, [projects, params.id, projectDetails?.projectName]);
    const dropdownRef = useRef(null);
    useEffect(() => {
        const el = dropdownRef.current;
        if (!el || !selectedProject)
            return;
        const rafId = requestAnimationFrame(() => {
            el.value = "";
            el.value = selectedProject;
        });
        return () => cancelAnimationFrame(rafId);
    }, [selectedProject]);
    const { data: networkQueryData, refetch: refetchNetworkDetails, status: networkApiStatus } = useProjectNetworkQuery(params.id, refetchDataFromApi);
    const assetNetworkDetails = (networkQueryData?.assetNetworkDetails ?? []);
    const showGhostAssetPhaseOne = appConfig?.release_1_1?.showGhostAssetPhaseOne;
    const displayableNetworkDetails = showGhostAssetPhaseOne
        ? assetNetworkDetails
        : assetNetworkDetails.filter((a) => a?.assetSourceType?.toUpperCase() !== "GHOST");
    const GhostTestProject = selectedProject === "GhostTest";
    const { data: { assetTrendsDetails = [] } = {}, isSuccess: assetTrendsDetailsApiSuccess } = useProjectPowerSocTrendsQuery(params.id, format(currentDay, "yyyy-MM-dd"), refetchDataFromApi);
    const { data: { assetEnergyActivities = [] } = {}, refetch: refetchEnergyActivities, status: assetEnergyActivitiesApiStatus } = useProjectEnergyActivitiesQuery(params.id, format(metricsDate, "yyyy-MM-dd"), fetchHistoryData, refetchDataFromApi);
    const assetId = "";
    const { data: { assetChargeActivities = [] } = {} } = useProjectAssetChargeActivitiesQuery(params.id, format(metricsDate, "yyyy-MM-dd"), assetId, fetchHistoryData, undefined, refetchDataFromApi);
    const { data: usageHistoryBarData, isError: historyDetailsApiError, refetch: refetchHistoryDetails, status: historyDetailsApiStatus } = useUsageHistoryDetailsQuery({
        aggregation: usageHistoryMetricsFilter.toLowerCase(),
        assetId,
        assetSourceType: "MACHINE",
        fetchHistoryData: historicChartPanelActiveIndex === 1 ? fetchHistoryData : false,
        id: params.id,
        refetchApiData: refetchDataFromApi,
        startDate: format(metricsDate, "yyyy-MM-dd")
    });
    const { data: essUsageHistoryData, refetch: refetchEssEnergyActivities, status: essEnergyActivitiesApiStatus } = useUsageHistoryDetailsQuery({
        aggregation: "hourly",
        assetId,
        assetSourceType: "ESS",
        fetchHistoryData,
        id: params.id,
        refetchApiData: refetchDataFromApi,
        refreshTimestamp: undefined,
        startDate: format(metricsDate, "yyyy-MM-dd")
    });
    const { data: machineUsageHistoryData, refetch: refetchMachineEnergyActivities, status: machineEnergyActivitiesApiStatus } = useUsageHistoryDetailsQuery({
        aggregation: "hourly",
        assetId,
        assetSourceType: "MACHINE",
        fetchHistoryData,
        id: params.id,
        refetchApiData: refetchDataFromApi,
        refreshTimestamp: undefined,
        startDate: format(metricsDate, "yyyy-MM-dd")
    });
    const toggleScreen = () => {
        setFullScreen(!isFullScreen);
    };
    const { data: assetDetailsResponse, refetch: refetchAssetDetails, status: assetDetailsApiStatus } = useProjectAssetsDetailsQuery(params.id, encodeProjectDetailsQuery(queryParamsAssetDetails), refetchDataFromApi);
    const { data: { energyMetrics = [], productivityMetrics = [] } = {}, refetch: refetchProjectEnergyMetrics, status: energyMetricsApiStatus } = useProjectEnergyMetricsQuery(params.id, format(metricsDate, "yyyy-MM-dd"), refetchDataFromApi);
    // Build a keyed map first so display order is derived from MetricsEnergyEnum once at
    // the end, rather than from sparse index assignment that silently breaks when enum
    // keys are reordered or when earlier metrics are absent from the API response.
    const metricsByKey = new Map();
    const energyMetricNames = energyMetrics?.[0] && Object.keys(energyMetrics[0]);
    energyMetricNames &&
        energyMetricNames.forEach((metricName) => {
            const metric = energyMetrics[0][metricName];
            const metricTitle = MetricsEnergyEnum[metricName];
            if (metric && metricTitle) {
                metricsByKey.set(metricName, {
                    currentDay: metric?.currentDay,
                    metric,
                    name: metricTitle
                });
            }
        });
    const showMergeButton = appConfig?.release_1_1?.showGhostAssetPhaseThree && catTabsActiveIndex === 0;
    const [showMergeOptions, setShowMergeOptions] = useState(false);
    const [hasSelectedAssets, setHasSelectedAssets] = useState(false);
    useEffect(() => {
        if (catTabsActiveIndex !== 0) {
            setShowMergeOptions(false);
            setHasSelectedAssets(false);
        }
    }, [catTabsActiveIndex]);
    const handleMergeClick = () => {
        setShowMergeOptions(true);
    };
    const handleCancel = () => {
        setShowMergeOptions(false);
        setHasSelectedAssets(false);
    };
    if (appConfig?.release_1_1?.showProductivity) {
        const mapProductivity = (prod, enumKey) => {
            if (!prod)
                return;
            metricsByKey.set(enumKey, {
                currentDay: prod.currentDay ?? null,
                metric: {
                    lastDay: {
                        energyTrend: prod.lastDay?.productivityTrend ?? null,
                        value: prod.lastDay?.value ?? null
                    },
                    lastMonth: {
                        energyTrend: prod.lastMonth?.productivityTrend ?? null,
                        value: prod.lastMonth?.value ?? null
                    },
                    lastWeek: {
                        energyTrend: prod.lastWeek?.productivityTrend ?? null,
                        value: prod.lastWeek?.value ?? null
                    }
                },
                name: MetricsEnergyEnum[enumKey]
            });
        };
        const prodMetrics = productivityMetrics?.[0];
        mapProductivity(prodMetrics?.haulerProductivity, "haulerDailyPrediction");
        mapProductivity(prodMetrics?.loaderProductivity, "loadOutDailyProduction");
        // Ensure both cards are always present (fall back to null if not in API response)
        const productivityEnumKeys = ["haulerDailyPrediction", "loadOutDailyProduction"];
        productivityEnumKeys.forEach((key) => {
            if (!metricsByKey.has(key)) {
                metricsByKey.set(key, {
                    currentDay: null,
                    metric: {
                        currentDay: null,
                        lastDay: { energyTrend: null, value: null },
                        lastMonth: { energyTrend: null, value: null },
                        lastWeek: { energyTrend: null, value: null }
                    },
                    name: MetricsEnergyEnum[key]
                });
            }
        });
    }
    // Reconstruct the ordered array using enum declaration order as the single source of truth.
    // Only present keys are included — no sparse holes.
    const energyMetricsData = Object.keys(MetricsEnergyEnum)
        .filter((key) => metricsByKey.has(key))
        .map((key) => metricsByKey.get(key));
    const { pathname } = useLocation();
    const dataLayer = (window["dataLayer"] = window["dataLayer"] || []);
    useEffect(() => {
        dataLayer.push({
            event: PageEvents.PAGE_VIEW,
            page_location: window.location.href, // setting the full url path
            page_title: "Project Details page"
        });
        dataLayer.push({
            event: "formVisible",
            formName: "live dashboard refresh"
        });
        const tab = searchParams.get("tab");
        // Triggering the form visibility event for GA when the refresh button is is visible or loaded
        dataLayer.push({
            event: "formVisibility",
            formLocation: "Topright",
            formName: !tab || tab === "Live" ? "live dashboard refresh" : "metrics-date-selection",
            pagePath: window.location.href
        });
    }, []);
    useEffect(() => {
        if (usageHistoryBarData && usageHistoryBarData.energyHistogram) {
            setUsageHistoryHistogramData(getBarChartData(usageHistoryBarData.energyHistogram[usageHistoryMetricsFilter.toLowerCase()], usageHistoryMetricsFilter));
            usageHistoryBarData?.energyHistogram[usageHistoryMetricsFilter.toLowerCase()]?.length > 0
                ? setUsageHistoryDataAvailable(true)
                : setUsageHistoryDataAvailable(false);
        }
    }, [usageHistoryBarData]);
    useEffect(() => {
        if (JSON.stringify(displayableNetworkDetails) !== JSON.stringify(networkDiagramData)) {
            setNetworkDiagramData(displayableNetworkDetails);
            setNetworkEdges(getNetworkEdges(displayableNetworkDetails));
            setNetworkNodes(getNetworkNodes(displayableNetworkDetails, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, undefined, projectDetailsTimezone));
        }
    }, [assetNetworkDetails, showGhostAssetPhaseOne]);
    useEffect(() => {
        if (JSON.stringify(assetTrendsDetails) !== JSON.stringify(assetTrendsData)) {
            setAssetTrendsData(assetTrendsDetails);
            if (assetNetworkDetails) {
                setNetworkNodes(getNetworkNodes(displayableNetworkDetails, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, undefined, projectDetailsTimezone));
            }
        }
    }, [assetTrendsDetails]);
    useEffect(() => {
        if (assetNetworkDetails &&
            projectDetails?.projectName &&
            projectDetails?.projectId &&
            assetTrendsDetailsApiSuccess &&
            assetTrendsDetails.length > 0) {
            setNetworkEdges(getNetworkEdges(displayableNetworkDetails));
            setNetworkNodes(getNetworkNodes(displayableNetworkDetails, projectDetails?.projectName, projectDetails?.projectId, projectDetails?.AssetDetails, assetTrendsDetails, undefined, projectDetailsTimezone));
        }
    }, [projectDetails, assetTrendsDetails, showGhostAssetPhaseOne]);
    const showMethanolReformer = appConfig?.release_1_1?.showMethanolReformer;
    const showMecCharger = appConfig?.release_1_1?.showMecCharger;
    const showProductivity = appConfig?.release_1_1?.showProductivity;
    useEffect(() => {
        const activitiesKey = JSON.stringify(assetEnergyActivities);
        const guardKey = `${activitiesKey}_${showMethanolReformer}_${showMecCharger}`;
        const prevGuardKey = `${JSON.stringify(assetEnergyActivityData)}_${showMethanolReformer}_${showMecCharger}`;
        if (guardKey !== prevGuardKey) {
            const preparedSankeyData = prepareSankeyData(assetEnergyActivities);
            const preparedSankeyChartData = prepareSankeyChartData(assetEnergyActivities, showMethanolReformer, showMecCharger);
            setSankeyData(preparedSankeyData);
            setSankeyChartData(preparedSankeyChartData);
            setAssetEnergyActivityData(assetEnergyActivities);
        }
    }, [assetEnergyActivities, showMethanolReformer, showMecCharger]);
    useEffect(() => {
        if (assetDetailsResponse) {
            const addressDetails = [];
            // The swagger contract renamed this field from `AssetDetails` to `assetDetails`.
            // Fall back to the legacy casing so the timeline still renders if the backend
            // has not been updated to the new contract yet.
            const assetDetailsList = assetDetailsResponse?.assetDetails ??
                assetDetailsResponse?.AssetDetails ??
                [];
            // Discard skeleton entries (e.g. [{}]) that the API occasionally returns —
            // an asset without an assetId is unusable and causes downstream crashes.
            const validAssetDetails = assetDetailsList.filter((a) => !!a?.assetId);
            if (GhostTestProject &&
                showGhostAssetPhaseOne &&
                !validAssetDetails.some((a) => a?.assetSourceType?.toUpperCase() === "GHOST")) {
                validAssetDetails.push(...GHOST_ASSET_ENTRIES);
            }
            validAssetDetails.forEach((assetDetails) => {
                if (!showGhostAssetPhaseOne && assetDetails?.assetSourceType?.toUpperCase() === "GHOST")
                    return;
                if (assetDetails?.location?.position?.coordinates.length) {
                    let lastReportedTime = assetDetails?.location?.lastReportedTime;
                    if (!appConfig?.release_1_1?.showCestTimezone &&
                        lastReportedTime &&
                        lastReportedTime.indexOf("T") === -1) {
                        const todayDate = new Date().toISOString().split("T")[0] + "T";
                        lastReportedTime = todayDate + lastReportedTime;
                    }
                    addressDetails.push({
                        activity: assetDetails?.activity,
                        assetId: assetDetails?.assetId,
                        assetMetadata: assetDetails?.assetMetadata,
                        assetName: assetDetails?.assetName || "",
                        iconUrl: getAssetMapIcon(assetDetails),
                        location: assetDetails?.location?.position?.coordinates,
                        soc: assetDetails?.soc,
                        streetAddress: assetDetails?.location?.streetAddress,
                        timestamp: lastReportedTime
                    });
                }
            });
            setAddressDetails(addressDetails);
            setProjectDetails({
                ...assetDetailsResponse,
                AssetDetails: validAssetDetails
            });
            setIsAddressDetailsLoaded(true);
        }
    }, [assetDetailsResponse, showGhostAssetPhaseOne, GhostTestProject]);
    useEffect(() => {
        if (JSON.stringify(assetChargeActivities) !== JSON.stringify(assetChargeActivityData)) {
            setAssetChargeActivityData(assetChargeActivities);
        }
    }, [assetChargeActivities]);
    useEffect(() => {
        const project = projects?.find((p) => p.projectId === params.id);
        if (project) {
            setProjectMetaData(project);
        }
    }, [projects, params.id]);
    const isNoDataAvailableForSankeyDiagram = useCallback(() => {
        //Atleast sankey digram needs two elements or its sum should be greater than zero
        const valueSum = sankeyData.links?.value.reduce((a, b) => a + b, 0);
        return ((sankeyData.links?.source?.length === 1 &&
            sankeyData.links?.target?.length === 1 &&
            sankeyData.links?.value?.length === 1 &&
            sankeyData.links?.value[0] < 1 &&
            (sankeyData.links?.source[0] !== sankeyData.links?.value[0] ||
                sankeyData.links?.target[0] !== sankeyData.links?.value[0])) ||
            valueSum === 0);
    }, [sankeyData]);
    const historicDataPanelChange = (event) => {
        setHistoricDataPanelActiveIndex(event.detail.activeIndex);
        switch (event.detail.activeIndex) {
            case 0:
                setHistoricDataPanel("Timeline");
                break;
            case 1:
                setHistoricDataPanel("SankeyDiagram");
                break;
            default:
                setHistoricDataPanel("Timeline");
                break;
        }
    };
    const historicChartPanelChange = (event) => {
        setHistoricChartPanelActiveIndex(event.detail.activeIndex);
        switch (event.detail.activeIndex) {
            case 0:
                setHistoricChartPanel("EnergyChart");
                break;
            case 1:
                setHistoricChartPanel("UsageHistory");
                break;
            default:
                setHistoricChartPanel("EnergyChart");
                break;
        }
    };
    const handleAiInsightsTabSelect = useCallback(() => {
        dataLayer.push({
            event: "formVisible",
            formName: "ai-insights-selection"
        });
        setCatTabsActiveIndex(3);
        setSearchParams({ tab: "AIInsights" });
    }, [dataLayer, setSearchParams]);
    const formatDate = (date) => {
        date.setDate(date.getDate() + 1);
        return format(date, "yyyy/MM/dd");
    };
    const onAssetSelect = (selected, forceSelected) => {
        const addressList = [];
        const updatedAssetDetails = projectDetails?.AssetDetails?.map((assetDetail) => {
            if (selected.assetId === assetDetail.assetId) {
                assetDetail.selected = forceSelected ?? !assetDetail.selected;
            }
            if (assetDetail.selected) {
                addressList.push(assetDetail.assetId);
            }
            return assetDetail;
        });
        if (addressList.length >= 1) {
            setSelectedAddress({ addressList });
        }
        else {
            setSelectedAddress({ addressList: [] });
        }
        projectDetails.AssetDetails = updatedAssetDetails;
        setProjectDetails(projectDetails);
        // Build a new nodes array with new node/data objects so React Flow reliably
        // re-renders the selection (grey) highlight. Mutating node.data in place and
        // re-using the same array reference caused nodes to intermittently keep or lose
        // the grey background when drawers were opened and closed.
        const updatedNodes = networkNodes.map((node) => node.id === selected.assetId
            ? {
                ...node,
                data: {
                    ...node.data,
                    selected: forceSelected ?? !node.data.selected
                }
            }
            : node);
        setNetworkNodes(updatedNodes);
    };
    const populateprojectsListDropdown = () => {
        return projects?.map((project) => {
            return (_jsx(CatListItem, { "data-test": `${project.projectName}-list-item`, onBlSelect: () => {
                    const currentParams = Object.fromEntries(searchParams.entries());
                    const queryString = new URLSearchParams(currentParams).toString();
                    navigate(`/project-details/${project.projectId}${queryString ? `?${queryString}` : ""}`);
                    dataLayer.push({
                        event: PageEvents.PAGE_VIEW,
                        page_location: window.location.href,
                        page_title: `${project.projectName} Project Details page`
                    });
                    setSelectedAddress({ addressList: [] });
                    setIsAddressDetailsLoaded(false);
                    handleReplayClick(0);
                }, value: {
                    label: project.projectName,
                    value: project.projectName
                } }, project.projectName));
        });
    };
    // Allow exiting Network Diagram full-screen with the Escape key and lock the
    // background scroll while the overlay is open. Scoped to full-screen so we
    // don't leak a new keydown listener on every render.
    const fullScreenOverlayRef = useRef(null);
    useEffect(() => {
        const dialog = fullScreenOverlayRef.current;
        if (!isFullScreen || !dialog)
            return undefined;
        // Open as a true modal: the browser handles focus-trapping and fires a
        // "cancel" event on Escape so we don't need a manual keydown listener.
        openDialogSafely(dialog);
        const previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        // Move focus into the overlay so keyboard panning/zoom works immediately,
        // even when full-screen was opened with the mouse.
        dialog.focus();
        return () => {
            closeDialogSafely(dialog);
            document.body.style.overflow = previousBodyOverflow;
        };
    }, [isFullScreen]);
    const getLiveDashBoardContent = () => {
        return (_jsxs("div", { "data-test": "live-dashboard-content", children: [_jsxs("div", { className: "flex mb-4 gap-2", "data-test": "live-dashboard-main-grid", children: [!isFullScreen && (_jsx("div", { className: "w-[36%] fixed-column-height border border-gray-300 overflow-y-auto overflow-x-hidden rounded resize-x", "data-test": "asset-portfolio-container", children: !isFullScreen && (_jsx(AssetPortfolio, { assetDetailsApiStatus: assetDetailsApiStatus, assetsDetails: projectDetails?.AssetDetails, enableDrawer: true, hideSOC: false, onAssetCheckboxChange: setHasSelectedAssets, onAssetSelect: onAssetSelect, projectId: params.id, projectMetaData: projectMetaData, refetchAssetDetails: refetchAssetDetails, setAssetsDetailsBySort: setAssetsDetailsBySort, setOrderByAsset: setOrderByAsset, setOrderBySoc: setOrderBySoc, showMergeOptions: showMergeOptions })) })), _jsxs("div", { className: "w-3/6 fixed-column-height border border-gray-300 rounded", "data-test": "network-diagram-container", children: [_jsx("div", { className: " border-b border-gray-300  p-2", "data-test": "network-diagram-header", children: _jsx(CatHeading, { "data-test": "network-diagram-title", variant: "title-sm", children: "Network Diagram" }) }), _jsx("div", { className: " rounded-b relative h-[92.5%] z-[400]", "data-test": "network-diagram-content", children: isFullScreen ? (_jsxs("dialog", { "aria-label": "Network Diagram full screen", className: "fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none flex-col border-0 bg-white p-4 outline-none", "data-test": "network-diagram-fullscreen", onCancel: (e) => {
                                            e.preventDefault();
                                            setFullScreen(false);
                                        }, ref: fullScreenOverlayRef, tabIndex: -1, children: [_jsxs("div", { className: "flex items-center justify-between border-b border-gray-300 pb-2 mb-2", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx(CatHeading, { "data-test": "network-diagram-fullscreen-title", variant: "title-sm", children: "Network Diagram" }), _jsx("span", { className: "text-xs text-gray-500", "data-test": "network-diagram-fullscreen-hint", children: "Use the arrow keys to pan, + / - to zoom, and Esc to exit." })] }), _jsxs(CatButton, { "aria-label": "Exit full screen", "data-test": "network-diagram-exit-fullscreen", onClick: toggleScreen, size: "sm", variant: "ghost", children: ["Exit Full Screen", _jsx(CatIconClose, { slot: "after" })] })] }), _jsx("div", { className: "relative flex-1 min-h-0", children: _jsx(NetworkDiagram, { networkApiStatus: networkApiStatus, onAssetSelect: onAssetSelect, onToggleScreen: toggleScreen, projectId: params.id, refetchNetworkDetails: refetchNetworkDetails }) })] })) : (_jsx(NetworkDiagram, { networkApiStatus: networkApiStatus, onAssetSelect: onAssetSelect, onToggleScreen: toggleScreen, projectId: params.id, refetchNetworkDetails: refetchNetworkDetails })) })] }), !isFullScreen && (_jsxs("div", { className: "w-3/6 column-height border border-gray-300 rounded relative", "data-test": "map-container", children: [_jsx("div", { className: " border-b border-gray-300  p-2 ", "data-test": "map-header", children: _jsx(CatHeading, { "data-test": "map-title", variant: "title-sm", children: "Map" }) }), addressDetails?.length > 0 ? (_jsx("div", { className: "rounded-b relative", "data-test": "map-content", children: _jsx(ProjectDetailsMaps, { addressDetails: addressDetails, selectedAddress: selectedAddress, siteOffset: projectMetaData?.offset, siteTimezone: projectMetaData?.timezone }, `live-map-${catTabsActiveIndex}`) })) : (((assetDetailsApiStatus === "success" &&
                                    addressDetails?.length === 0 &&
                                    isAddressDetailsLoaded) ||
                                    assetDetailsApiStatus === "error") && (_jsx("div", { className: "rounded-b relative flex justify-center items-center h-[92.5%] min-h-[40vh] text-xl text-center", "data-test": "map-content", children: _jsx("span", { children: "No Data Available" }) })))] }))] }), catTabsActiveIndex === 4 && (_jsx(SankeyDiagram, { customData: sankeyData.customData, "data-test": "live-sankey-diagram", labels: sankeyData.labels, links: sankeyData.links, positions: sankeyData.positions }))] }));
    };
    const getHistoricData = () => {
        const wideRightPanel = !!replayTimeline || !!showProductivity;
        const isProductivityRightPanel = !!showProductivity && !(historicDataPanel === "Timeline" && replayTimeline);
        const leftColumnWidthClass = wideRightPanel ? "w-2/3" : "w-3/4";
        const rightColumnWidthClass = wideRightPanel ? "w-1/3" : "w-1/4";
        return (_jsxs("div", { className: "flex mb-4 gap-2", "data-test": "historic-data-content", children: [_jsxs("div", { className: "fixed-column-height flex flex-col justify-between " + leftColumnWidthClass, "data-test": "historic-timeline-container", children: [_jsxs("div", { className: "max-h-[640px] border border-gray-300 rounded flex flex-col", "data-test": "historic-activity-panel", children: [_jsxs("div", { className: "flex justify-between mb-2 border-b p-2", "data-test": "historic-panel-header", children: [_jsxs(CatHeading, { className: "flex", "data-test": "project-history-data-title", variant: "title", children: [historicDataPanel === "Timeline" && _jsx(_Fragment, { children: "Activity Timeline" }), historicDataPanel === "SankeyDiagram" && _jsx(_Fragment, { children: "Energy Flow" })] }), _jsx("div", { className: "flex", "data-test": "historic-panel-switcher", children: _jsxs(CatSegmentedControl, { activeIndex: historicDataPanelActiveIndex, className: "relative", "data-test": "timeline-sankey-segmented-control", onBlSegmentItemChange: historicDataPanelChange, children: [_jsx(CatSegmentedControlItem, { "data-test": "timeline-title-tab", onClick: () => {
                                                            handleReplayClick(0);
                                                            setPanel("Timeline");
                                                        }, children: "Timeline" }), _jsx(CatSegmentedControlItem, { "data-test": "sankey-diagram-tab", onClick: () => setPanel("SankeyDiagram"), children: "Sankey Diagram" })] }) })] }), historicDataPanel === "Timeline" && (_jsx("div", { "data-test": "timeline-panel", children: _jsx("div", { className: "mb-1", "data-test": "activity-timeline-wrapper", children: _jsx(ActivityTimeline, { assetChargeActivityData: assetChargeActivityData, assetDetailsApiStatus: assetDetailsApiStatus, assetsDetails: projectDetails?.projectId === params.id &&
                                                assetDetailsApiStatus === "success"
                                                ? projectDetails?.AssetDetails
                                                : undefined, metricsDate: metricsDate, refetchAssetDetails: refetchAssetDetails, replayTimeline: replayTimeline, setReplayTimelineValue: setReplayTimelineValue }, params.id) }) })), historicDataPanel === "SankeyDiagram" && (_jsxs("div", { className: "flex mb-4", "data-test": "sankey-panel", children: [_jsx("div", { className: "w-1/4 h-[530px] border-r border-gray-300 overflow-y-auto rounded mr-2", "data-test": "sankey-asset-list", children: _jsx(AssetPortfolio, { assetDetailsApiStatus: assetDetailsApiStatus, assetsDetails: projectDetails?.AssetDetails, enableDrawer: false, hideSOC: true, hideTitle: true, onAssetSelect: onAssetSelect, projectId: params.id, projectMetaData: projectMetaData, refetchAssetDetails: refetchAssetDetails, setAssetsDetailsBySort: setAssetsDetailsBySort, setOrderByAsset: setOrderByAsset, setOrderBySoc: setOrderBySoc }) }), _jsxs("div", { className: "w-3/4 h-[530px] mr-2", "data-test": "sankey-diagram-area", children: [assetEnergyActivitiesApiStatus === "success" &&
                                                    assetEnergyActivities?.length === sankeyData?.links?.source?.length &&
                                                    (sankeyData.customData.length === 0 ||
                                                        isNoDataAvailableForSankeyDiagram() ||
                                                        !sankeyChartData?.nodes?.length ||
                                                        !sankeyChartData?.links?.length) && (_jsx(NoDataMessage, { "data-test": "sankey-no-data", date: metricsDate })), assetEnergyActivitiesApiStatus === "success" &&
                                                    assetEnergyActivities?.length === sankeyData?.links?.source?.length &&
                                                    !(sankeyData.customData.length === 0 || isNoDataAvailableForSankeyDiagram()) &&
                                                    sankeyChartData?.nodes?.length > 0 &&
                                                    sankeyChartData?.links?.length > 0 && (_jsx(SankeyChart, { data: sankeyChartData, projectId: projectDetails?.projectId, projectName: projectDetails?.projectName })), assetEnergyActivitiesApiStatus === "pending" && (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh]", "data-test": "sankey-loading", children: _jsx(CatLoadingIndicator, {}) })), assetEnergyActivitiesApiStatus === "error" && (_jsxs("div", { className: "flex justify-center items-center h-full min-h-[28vh]", "data-test": "sankey-error", children: [_jsx(APIError, { refreshHandler: refetchEnergyActivities }), ";"] }))] })] }))] }), historicDataPanel === "Timeline" && _jsx(Legend, {})] }), _jsx("div", { className: `${isProductivityRightPanel
                        ? "mr-1 relative"
                        : "fixed-column-height mr-1 border border-gray-300 rounded relative"} ${rightColumnWidthClass}`, children: historicDataPanel === "Timeline" && replayTimeline ? (_jsxs("div", { className: "w-full fixed-column-height", children: [_jsx("div", { className: " border-b border-gray-300  p-2", children: _jsx(CatHeading, { "data-test": "network-diagram-title", variant: "title-sm", children: "Network Diagram" }) }), _jsx("div", { className: "rounded-b relative h-[92.5%]", children: _jsx(NetworkDiagram, { networkApiStatus: networkApiStatus, onAssetSelect: onAssetSelect, onToggleScreen: toggleScreen, projectId: params.id, refetchNetworkDetails: refetchNetworkDetails, replayTimeline: replayTimeline, replayTimelineValue: replayTimelineValue }) })] })) : (_jsx(_Fragment, { children: showProductivity ? (_jsx(EnergyProductivity, { date: metricsDate, projectId: params.id })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex justify-end mb-2 border-b p-2", children: _jsxs(CatSegmentedControl, { activeIndex: historicChartPanelActiveIndex, className: "relative", onBlSegmentItemChange: historicChartPanelChange, children: [_jsx(CatSegmentedControlItem, { "data-test": "energy-chart-tab", children: "Energy Chart" }), _jsx(CatSegmentedControlItem, { "data-test": "machine-usage-history-tab", children: "Machine Usage History" })] }) }), historicChartPanel === "EnergyChart" && (_jsx("div", { className: "h-[85%]", "data-test": "energy-chart", children: _jsx(EnergyChart, { date: metricsDate, essEnergyActivitiesApiStatus: essEnergyActivitiesApiStatus, essUsage: extractHourlyEnergyData(essUsageHistoryData), machineEnergyActivitiesApiStatus: machineEnergyActivitiesApiStatus, machineUsage: extractHourlyEnergyData(machineUsageHistoryData), refetchEssEnergyActivities: refetchEssEnergyActivities, refetchMachineEnergyActivities: refetchMachineEnergyActivities }) })), historicChartPanel === "UsageHistory" && (_jsx("div", { children: _jsx(UsageHistory, { historyDetailsApiError: historyDetailsApiError, historyDetailsApiStatus: historyDetailsApiStatus, refetchHistoryDetails: refetchHistoryDetails, setUsageHistoryMetricsFilter: setUsageHistoryMetricsFilter, usageHistoryDataAvailable: usageHistoryDataAvailable, usageHistoryHistogramData: usageHistoryHistogramData, usageHistoryMetricsFilter: usageHistoryMetricsFilter }) }))] })) })) })] }));
    };
    const getOpsPlannerContent = () => {
        const assetsIds = [];
        const { AssetDetails = [] } = projectDetails || {};
        const machineAssetDetails = AssetDetails.filter((assetDetails) => assetDetails?.assetSourceType?.toUpperCase() === "MACHINE") || [];
        machineAssetDetails?.map((assetInfo) => {
            assetsIds.push(assetInfo.assetId);
        });
        if (assetsIds.length <= 0 && catTabsActiveIndex === 2) {
            return _jsx(_Fragment, {});
        }
        return (_jsx("div", { className: "flex-1 mb-4", children: _jsx(OpsPlanner, { addressDetails: addressDetails, assetsDetails: machineAssetDetails, assetsIds: assetsIds, fetchChargingData: catTabsActiveIndex === 2, projectMetaData: projectMetaData, refetchDataFromApi: refetchDataFromApi, setOpsPlannerDate: setOpsPlannerDate }) }));
    };
    const Legend = () => {
        const legendItems = [
            { color: "bg-[#1AC69E]", label: "Charging" },
            { color: "bg-[#FB5A00]", label: "Discharging" },
            { color: "bg-[#2980B9]", label: "Working" },
            { color: "bg-[#8E44AD]", label: "Self Discharge" },
            { color: "bg-[#8CBAFF]", label: "Idle / Available" },
            { color: "bg-[#91F2E6]", label: "Trickle" },
            { color: "bg-[#990000]", label: "Faulted" },
            { color: "bg-[#F2F2F2]", label: "Off/Not Reporting" },
            { color: "bg-[#9CA3AF]", label: "Unavailable" }
        ];
        return (_jsxs("div", { className: "flex px-2 py-2 border border-gray-300 rounded w-full items-center justify-center", "data-test": "timeline-legend", children: [_jsx("span", { className: "text-md mr-2", "data-test": "timeline-legend-label", children: "Legend:" }), _jsx("span", { className: "flex flex-wrap text-md", "data-test": "timeline-legend-items", children: legendItems.map((item, index) => {
                        const key = item.label
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/-$/, "");
                        return (_jsxs("div", { className: "flex items-center mr-4 whitespace-nowrap", "data-test": `timeline-legend-item-${key}`, children: [_jsx("div", { className: `w-2.5 h-2.5 rounded-full mr-0.5 ${item.color}`, "data-test": `timeline-legend-dot-${key}` }), _jsx("span", { "data-test": `timeline-legend-text-${key}`, children: item.label })] }, index));
                    }) })] }));
    };
    return (_jsxs("div", { className: "flex-1 wrapper m-4", "data-test": "project-details-container", children: [_jsxs("div", { className: "inline-flex justify-between", "data-test": "breadcrumb-navigation", children: [_jsxs("ol", { "aria-label": "Breadcrumb", className: "flex items-center mb-2", "data-test": "project-breadcrumb", children: [_jsx(CatIconArrowCircleLeft, { className: "cursor-pointer", "data-test": "cat-icon-arrow-circle-left", onClick: () => navigate(CaasRoute.Home), size: "xl" }), _jsx("li", { className: "inline-flex items-center py-1.5 px-2", "data-test": "project-name-label-container", children: _jsx("a", { className: "flex items-center text-sm text-gray-500", "data-test": "project-name-label", href: "#", children: "Project Name:" }) }), _jsx("li", { "aria-current": "page", className: "inline-flex items-center py-1.5 px-2 text-base font-semibold text-gray-800", "data-test": "project-dropdown-container", children: _jsx("div", { className: "min-w-[190px] max-w-[400px]", children: _jsx(CatDropdown, { ariaDescribedBy: "dropdown-field-aria-1", className: "w-full", "data-test": "Cat-projects-Dropdown", fieldId: "dropdown-1", placeholder: "Loading projects...", ref: dropdownRef, size: "sm", value: selectedProject, children: _jsx(CatList, { "data-test": "Cat-projects-List", children: populateprojectsListDropdown() }) }) }) })] }), _jsx("ol", { "aria-label": "Breadcrumb", className: "flex items-center mb-2", "data-test": "metrics-breadcrumb", children: catTabsActiveIndex === 1 ? (_jsxs("ol", { "aria-label": "Breadcrumb", className: "flex items-center mb-2", "data-test": "historic-metrics-breadcrumb", children: [!isFullScreen && (_jsx("li", { className: "inline-flex items-center py-1.5 px-2", "data-test": "historic-metrics-label-container", children: _jsx("a", { className: "flex items-center text-sm text-gray-500", "data-test": "show-metrics-for-label", href: "#", children: "Show metrics for:" }) })), _jsx("li", { "aria-current": "page", className: "inline-flex items-center py-1.5 px-2 text-gray-800", "data-test": "historic-date-picker-container", children: !isFullScreen && (_jsx("form", { "data-test": "metrics-date-selection-form", id: "metrics-date-selection", name: "metrics-date-picker", children: _jsx("div", { className: "datepicker-field-container", "data-test": "datepicker-wrapper", id: "datepicker-field-wrapper", children: _jsx(CatDatepickerField, { ariaDescribedBy: "metrics-datepicker-field-aria-1", className: "metrics-datepicker", "data-test": "metrics-datepicker-field", dateFormat: "MM/dd/yyyy", disabledMaxDate: formatDate(new Date()), fieldId: "metrics-datepicker-field-1", name: "metricsDatePicker", onBlDatePickerChange: (event) => {
                                                    setHistoricDataDate(event?.detail?.date);
                                                    setMetricsDate(event?.detail?.date);
                                                    setSelectedDate(format(event?.detail?.date, "yyyy-MM-dd"));
                                                    handleReplayClick(0);
                                                    dataLayer.push({
                                                        event: PageEvents.FORM_SUBMITTED,
                                                        formAdditionalContent: null,
                                                        formContent: event?.detail?.date,
                                                        formContent2: null,
                                                        formDestination: window.location.href,
                                                        formFieldCausingError: null,
                                                        formId: "metrics-date-selection",
                                                        formLength: 1,
                                                        formLocation: "historic data tab",
                                                        formName: "metrics-date-selection",
                                                        formStatus: "success",
                                                        formSubmitText: "Show metrics for",
                                                        pagePath: pathname ?? ""
                                                    });
                                                }, onBlDatePickerOpen: () => {
                                                    // Triggering the form visibility event for GA when the metrics date selection button is clicked
                                                    dataLayer.push({
                                                        event: "formStart",
                                                        formDestination: window.location.href,
                                                        formId: "metrics-date-selection",
                                                        formLength: 1,
                                                        formName: "metrics-date-selection"
                                                    });
                                                }, placeholder: "Select date", size: "sm", value: metricsDate }) }) })) })] })) : (_jsxs(_Fragment, { children: [_jsx("li", { className: "inline-flex items-center py-1.5 px-2", "data-test": "live-metrics-display-container", children: _jsxs("div", { className: "flex items-center text-sm text-gray-500", "data-test": "metrics-label", children: [_jsx("span", { "data-test": "showing-metrics-for-label", children: " Showing metrics for: " }), _jsx("span", { "data-test": "metrics-date-display", children: catTabsActiveIndex === 2 ? opsPlannerDate : liveDashboardDate })] }) }), _jsx("form", { "data-test": "live-dashboard-refresh-form", id: "live dashboard refresh", name: "refresh", children: _jsxs(CatButton, { className: "inline-flex", "data-test": "refresh-button", onClick: () => {
                                            setRefetchDataFromApi(new Date().getTime());
                                            // For refresh as it was click event we need to pass
                                            // both form start and form submitted event simultaneously
                                            dataLayer.push({
                                                event: "formStart",
                                                formDestination: window.location.href,
                                                formId: "live dashboard refresh",
                                                formLength: 1,
                                                formName: "live dashboard refresh"
                                            });
                                            dataLayer.push({
                                                event: PageEvents.FORM_SUBMITTED,
                                                formAdditionalContent: null,
                                                formContent: "refreshing the live dashboard data",
                                                formContent2: null,
                                                formDestination: window.location.href,
                                                formFieldCausingError: null,
                                                formId: "live dashboard refresh",
                                                formLength: 1,
                                                formLocation: "live dashboard tab",
                                                formName: "live dashboard refresh",
                                                formStatus: "success",
                                                formSubmitText: "Refresh",
                                                pagePath: pathname ?? ""
                                            });
                                        }, size: "sm", children: ["Refresh", _jsx(CatIconReorder, { className: "inline-flex justify-center pl-1 h-3.5" })] }) })] })) })] }), _jsx("div", { className: "border-t", "data-test": "tabs-container", children: _jsxs(CatTabs, { activeIndex: catTabsActiveIndex, "data-test": "dashboard-panel", children: [_jsxs(React.Fragment, { children: [_jsx(CatTab, { "data-test": "live-dashboard-tab", index: 0, onBlTabSelect: () => {
                                        setSearchParams({ tab: "Live" });
                                        dataLayer.push({
                                            event: "formVisible",
                                            formName: "live dashboard refresh"
                                        });
                                        // Triggering the form visibility event for GA when the live dashboard refresh button is visible or loaded
                                        dataLayer.push({
                                            event: "formVisibility",
                                            formLocation: "Topright",
                                            formName: "live dashboard refresh",
                                            pagePath: window.location.href
                                        });
                                        setMetricsDate(liveDashboardDate);
                                        setCatTabsActiveIndex(0);
                                        setFetchHistoryData(false);
                                        handleReplayClick(0);
                                    }, children: "Live Dashboard" }), _jsx(CatTabPanel, { "data-test": "live-dashboard-panel", slot: "panel", children: getLiveDashBoardContent() }), _jsxs(CatTab, { "data-test": "historic-data-tab", index: 1, onBlTabSelect: () => {
                                        setSearchParams({ tab: "Historic" });
                                        dataLayer.push({
                                            event: "formVisible",
                                            formName: "metrics-date-selection"
                                        });
                                        // Triggering the form visibility event for GA when the metrics date selection button is is visible or loaded
                                        dataLayer.push({
                                            event: "formVisibility",
                                            formLocation: "Topright",
                                            formName: "metrics-date-selection",
                                            pagePath: window.location.href
                                        });
                                        setMetricsDate(historicDataDate ? historicDataDate : formattedPreviousDay);
                                        setCatTabsActiveIndex(1);
                                        setFetchHistoryData(true);
                                    }, children: ["Historic Data", !appConfig?.release_1_1?.showCestTimezone && (_jsxs(CatTooltip, { "data-test": "timezone-tooltip", children: [_jsx(CatIconInfo, { className: "inline-flex justify-center pl-1 h-3.5", "data-test": "timezone-info-icon", slot: "trigger" }), _jsx("span", { "data-test": "timezone-tooltip-text", children: "All Timezones are in UTC" })] }))] }), _jsx(CatTabPanel, { "data-test": "historic-data-tab-panel", slot: "panel", children: getHistoricData() }), _jsx(CatTab, { "data-test": "ops-planner-tab", index: 2, onBlTabSelect: () => {
                                        dataLayer.push({
                                            event: "formVisible",
                                            formName: "ops-planner-selection"
                                        });
                                        setCatTabsActiveIndex(2);
                                        setSearchParams({ tab: "OpsPlanner" });
                                    }, children: "Ops Planner" }), _jsx(CatTabPanel, { "data-test": "ops-planner-panel", slot: "panel", children: getOpsPlannerContent() })] }, ".0"), appConfig?.release_1_1?.showAiInsights && isCatUser && (_jsxs(_Fragment, { children: [_jsx(CatTab, { "data-test": "ai-insights-tab", index: 3, onBlTabSelect: handleAiInsightsTabSelect, children: "AI Insights" }), _jsx(CatTabPanel, { "data-test": "ai-insights-tab-panel", slot: "panel", children: _jsx(AiAssistant, {}) })] })), appConfig?.release_1_1?.showReplaySlider && catTabsActiveIndex === 1 ? (_jsx(CatSegmentedControl, { activeIndex: historicDataPanelActiveIndex, className: "absolute right-0 top-3", "data-test": "replay-segmented-control", onBlSegmentItemChange: historicDataPanelChange, children: _jsx(CatSegmentedControlItem, { "data-test": "replay-button", onClick: () => {
                                    handleReplayClick(1);
                                    setPanel("Replay");
                                }, children: "Replay" }) })) : null, _jsx(AssetMergeButton, { hasSelectedAssets: hasSelectedAssets, onCancel: handleCancel, onMergeClick: handleMergeClick, showMergeButton: showMergeButton, showMergeOptions: showMergeOptions })] }) }), (catTabsActiveIndex === 0 || catTabsActiveIndex === 1) && (_jsx("div", { children: _jsx(Metrics, { energyMetricsApiStatus: energyMetricsApiStatus, energyMetricsData: energyMetricsData, metricsDate: metricsDate, refetchProjectEnergyMetrics: refetchProjectEnergyMetrics }) }))] }));
};
export default ProjectDetails;
